// L'EFFACEUR — Panel admin 2FA TOTP setup

import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { Shield, ShieldCheck, ShieldOff, Copy, AlertTriangle, CheckCircle } from 'lucide-react';
import { API } from '../../App';
import { Button } from '../ui/button';

const TwoFactorSetup = () => {
  const [status, setStatus] = useState('loading'); // loading | not_enabled | pending | enabled
  const [setupData, setSetupData] = useState(null); // { secret, otpauth_url }
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [backupCodes, setBackupCodes] = useState(null);
  const [showDisable, setShowDisable] = useState(false);
  const [disableCode, setDisableCode] = useState('');

  const fetchStatus = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } });
      const me = res.data;
      if (me.twofa_enabled) setStatus('enabled');
      else setStatus('not_enabled');
    } catch (e) {
      setStatus('not_enabled');
    }
  }, []);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  const startSetup = async () => {
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/security/2fa/setup`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setSetupData(res.data);
      setStatus('pending');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur setup 2FA');
    } finally {
      setSubmitting(false);
    }
  };

  const verifyCode = async () => {
    if (code.length !== 6) {
      toast.error('Code à 6 chiffres requis');
      return;
    }
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/security/2fa/verify`, { code }, { headers: { Authorization: `Bearer ${token}` } });
      setBackupCodes(res.data.backup_codes);
      setStatus('enabled');
      toast.success('2FA activé');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Code invalide');
    } finally {
      setSubmitting(false);
    }
  };

  const disableTwoFA = async () => {
    if (disableCode.length !== 6) {
      toast.error('Code à 6 chiffres requis');
      return;
    }
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/security/2fa/disable`, { code: disableCode }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('2FA désactivé');
      setStatus('not_enabled');
      setShowDisable(false);
      setDisableCode('');
      setSetupData(null);
      setBackupCodes(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Code invalide');
    } finally {
      setSubmitting(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard?.writeText(text);
    toast.success('Copié');
  };

  // QR code via service externe (api.qrserver.com — pas de dépendance lib)
  const qrUrl = setupData?.otpauth_url
    ? `https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encodeURIComponent(setupData.otpauth_url)}`
    : null;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-1 h-8 bg-cyan-400 rounded-full" style={{ boxShadow: '0 0 10px rgba(0,245,255,0.8)' }} />
        <h2 className="font-orbitron text-xl font-black text-white">Authentification 2FA</h2>
        <span className="font-mono text-xs text-cyan-400/60">// SECURITY · TOTP</span>
      </div>

      {status === 'loading' && (
        <div className="text-gray-500 font-mono text-sm">Chargement...</div>
      )}

      {status === 'not_enabled' && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="glass-card border border-gray-800 p-6">
          <div className="flex items-start gap-4 mb-4">
            <ShieldOff className="w-10 h-10 text-orange-400 shrink-0" />
            <div>
              <h3 className="text-white font-bold mb-1">2FA non activé</h3>
              <p className="text-gray-400 text-sm">
                Active la double authentification pour sécuriser ton compte admin. Tu auras besoin d'une app comme
                Google Authenticator, Authy, ou 1Password.
              </p>
            </div>
          </div>
          <Button onClick={startSetup} disabled={submitting} className="bg-cyan-400 text-black hover:bg-cyan-300 font-bold">
            <Shield className="w-4 h-4 mr-2" /> {submitting ? 'Initialisation...' : 'Activer 2FA'}
          </Button>
        </motion.div>
      )}

      {status === 'pending' && setupData && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="glass-card border border-cyan-400/40 p-6 space-y-4">
          <h3 className="text-white font-bold flex items-center gap-2">
            <Shield className="w-5 h-5 text-cyan-400" /> Configuration en cours
          </h3>

          <div className="text-sm text-gray-300 space-y-2">
            <p><span className="text-cyan-400 font-mono">1.</span> Ouvre ton app authenticator (Google Authenticator, Authy, 1Password...)</p>
            <p><span className="text-cyan-400 font-mono">2.</span> Scanne le QR code OU entre le secret manuellement</p>
            <p><span className="text-cyan-400 font-mono">3.</span> Entre le code à 6 chiffres généré pour valider</p>
          </div>

          <div className="flex flex-col md:flex-row gap-4 items-start">
            {qrUrl && (
              <div className="bg-white p-3 rounded shrink-0">
                <img src={qrUrl} alt="QR Code 2FA" width="220" height="220" />
              </div>
            )}
            <div className="flex-1 space-y-3 w-full">
              <div>
                <label className="block text-xs font-mono text-gray-400 uppercase mb-1">Secret manuel</label>
                <div className="flex gap-2">
                  <input
                    readOnly
                    value={setupData.secret}
                    className="flex-1 px-3 py-2 bg-gray-900 border border-gray-700 rounded text-cyan-400 text-sm font-mono"
                  />
                  <button
                    onClick={() => copyToClipboard(setupData.secret)}
                    className="px-3 py-2 bg-gray-800 border border-gray-700 rounded text-gray-300 hover:text-cyan-400"
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-xs font-mono text-gray-400 uppercase mb-1">Code à 6 chiffres</label>
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                  placeholder="123456"
                  className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded text-white text-center text-2xl font-mono tracking-widest focus:border-cyan-400 focus:outline-none"
                />
              </div>

              <Button
                onClick={verifyCode}
                disabled={submitting || code.length !== 6}
                className="w-full bg-cyan-400 text-black hover:bg-cyan-300 font-bold"
              >
                {submitting ? 'Vérification...' : 'Activer 2FA'}
              </Button>
            </div>
          </div>
        </motion.div>
      )}

      {status === 'enabled' && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
          <div className="glass-card border border-success/40 p-6">
            <div className="flex items-start gap-4">
              <ShieldCheck className="w-10 h-10 text-success shrink-0" />
              <div className="flex-1">
                <h3 className="text-white font-bold mb-1">2FA activé</h3>
                <p className="text-gray-400 text-sm">
                  À chaque connexion, tu devras entrer un code généré par ton app authenticator.
                </p>
              </div>
            </div>
          </div>

          {backupCodes && (
            <div className="glass-card border border-orange-400/40 p-6">
              <div className="flex items-start gap-3 mb-3">
                <AlertTriangle className="w-6 h-6 text-orange-400 shrink-0" />
                <div>
                  <h3 className="text-white font-bold mb-1">Codes de secours — À NOTER MAINTENANT</h3>
                  <p className="text-orange-400/80 text-sm">
                    Ces codes ne seront plus jamais affichés. Note-les dans un endroit sûr (gestionnaire de mots de passe, papier).
                    Chaque code est utilisable une seule fois si tu perds ton téléphone.
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-3">
                {backupCodes.map((c, i) => (
                  <div key={i} className="bg-gray-900 border border-gray-700 rounded px-2 py-1 text-cyan-400 font-mono text-sm text-center">
                    {c}
                  </div>
                ))}
              </div>
              <Button
                onClick={() => copyToClipboard(backupCodes.join('\n'))}
                variant="outline"
                className="text-xs"
              >
                <Copy className="w-3 h-3 mr-1" /> Copier tous les codes
              </Button>
            </div>
          )}

          <div className="glass-card border border-gray-800 p-6">
            <h3 className="text-white font-bold mb-3">Désactiver le 2FA</h3>
            {!showDisable ? (
              <Button
                onClick={() => setShowDisable(true)}
                variant="outline"
                className="text-red-400 border-red-400/40 hover:bg-red-400/10"
              >
                <ShieldOff className="w-4 h-4 mr-2" /> Désactiver
              </Button>
            ) : (
              <div className="space-y-3">
                <p className="text-sm text-gray-400">Entre ton code 2FA actuel pour confirmer la désactivation.</p>
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={disableCode}
                  onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, ''))}
                  placeholder="123456"
                  className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded text-white text-center text-xl font-mono tracking-widest"
                />
                <div className="flex gap-2">
                  <Button onClick={() => { setShowDisable(false); setDisableCode(''); }} variant="outline">
                    Annuler
                  </Button>
                  <Button
                    onClick={disableTwoFA}
                    disabled={submitting || disableCode.length !== 6}
                    className="bg-red-500 text-white hover:bg-red-600 font-bold"
                  >
                    {submitting ? 'Désactivation...' : 'Confirmer la désactivation'}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default TwoFactorSetup;
